import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'

import { useReselector } from '@/utils/sharedHooks'
import { useAppDispatch } from '@/utils/typeHelpers'
import {
  selectBookkeepingDocuments,
  selectIncorporationDocuments,
  selectOtherDocuments,
  selectReceiptDocuments,
  selectTaxDocuments,
  selectUserBankDocuments,
} from './lib/userDocuments.selector'

import {
  sortDocumentsByFileNameAsc,
  sortDocumentsByNameAscWithinCreatedAtDesc,
  sortDocumentsByNameAscWithinStatementDateDesc,
} from './lib/helpers'

import {
  BankStatements,
  Bookkeeping,
  Tax,
  Incorporation,
  Receipts,
  Other,
} from './tabs'

import { useAnalyticsView } from '@/features/amplitude'
import { PageHeader } from '@/components/PageHeader'
import { FeatureStateProvider } from './lib/state'
import { fetchUserDocuments } from './lib/userDocuments.slice'

import { GroupByEnum } from './table/helpers'
import { Tabs } from '@/ui'
import { ConfirmDeleteDocumentDialog } from './shared/ConfirmDeleteDocumentDialog'

// TODO: Migrate to V2 // skipcq: JS-0002
import { fetchUserDocumentCategoriesIfNeeded } from '@/featuresV1/Admin/UserDocumentCategories/userDocumentCategories.slice'

export const Documents = () => {
  const bankStatementDocuments = useReselector(selectUserBankDocuments)
  const bookkeepingDocuments = useReselector(selectBookkeepingDocuments)
  const taxRelatedDocuments = useReselector(selectTaxDocuments)
  const incorporationDocuments = useReselector(selectIncorporationDocuments)
  const receiptDocuments = useReselector(selectReceiptDocuments)
  const otherDocuments = useReselector(selectOtherDocuments)
  const [searchParams, setSearchParams] = useSearchParams()
  const defaultTab = searchParams.get('tab')
  const dispatch = useAppDispatch()
  const pageView = useAnalyticsView()

  const [activeTab, setActiveTab] = useState(() => {
    switch (defaultTab) {
      case 'bank-statements':
        return 'bank-statements'
      case 'bookkeeping':
        return 'bookkeeping'
      case 'taxes':
        return 'taxes'
      case 'incorporation':
        return 'incorporation'
      case 'receipts':
        return 'receipts'
      case 'other':
        return 'other'
      default:
        return 'bank-statements'
    }
  })

  const updateTab = (value: string) => {
    setSearchParams({ tab: value }, { replace: true })
    setActiveTab(value)
  }

  useEffect(() => {
    pageView('documents')
  }, [pageView])

  useEffect(() => {
    dispatch(fetchUserDocuments())
    dispatch(fetchUserDocumentCategoriesIfNeeded())
  }, [dispatch])

  return (
    <div>
      <FeatureStateProvider>
        <PageHeader header="Your Documents" />

        <Tabs value={activeTab} onValueChange={updateTab}>
          <Tabs.List>
            <Tabs.Trigger value="bank-statements">Bank Statements</Tabs.Trigger>
            <Tabs.Trigger value="bookkeeping">Bookkeeping</Tabs.Trigger>
            <Tabs.Trigger value="taxes">Taxes</Tabs.Trigger>
            <Tabs.Trigger value="incorporation">Incorporation</Tabs.Trigger>
            <Tabs.Trigger value="receipts">Receipts</Tabs.Trigger>
            <Tabs.Trigger value="other">Other</Tabs.Trigger>
          </Tabs.List>

          <Tabs.Content value="bank-statements">
            <BankStatements
              documents={bankStatementDocuments}
              tableOptions={{
                sortFn: sortDocumentsByNameAscWithinStatementDateDesc,
                groupBy: GroupByEnum.StatementYear,
              }}
            />
          </Tabs.Content>

          <Tabs.Content value="bookkeeping">
            <Bookkeeping
              documents={bookkeepingDocuments}
              tableOptions={{
                sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
                groupBy: GroupByEnum.CreatedAtYear,
              }}
            />
          </Tabs.Content>

          <Tabs.Content value="taxes">
            <Tax
              documents={taxRelatedDocuments}
              tableOptions={{
                sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
                groupBy: GroupByEnum.CategoryYear,
              }}
            />
          </Tabs.Content>

          <Tabs.Content value="incorporation">
            <Incorporation
              documents={incorporationDocuments}
              tableOptions={{
                sortFn: sortDocumentsByFileNameAsc,
                groupBy: GroupByEnum.None,
              }}
            />
          </Tabs.Content>

          <Tabs.Content value="receipts">
            <Receipts
              documents={receiptDocuments}
              tableOptions={{
                sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
                groupBy: GroupByEnum.MonthWithinYear,
              }}
            />
          </Tabs.Content>

          <Tabs.Content value="other">
            <Other
              documents={otherDocuments}
              tableOptions={{
                sortFn: sortDocumentsByNameAscWithinCreatedAtDesc,
                groupBy: GroupByEnum.None,
              }}
            />
          </Tabs.Content>
        </Tabs>
        <ConfirmDeleteDocumentDialog />
      </FeatureStateProvider>
    </div>
  )
}
